import type { UserStateEntry } from "../types/user-state";

/**
 * Pure port of `useUserJobStateSync.ts`'s conflict-merge behavior (TEC-404): when a tracked-job
 * write (applicationState, notes, tags, checklist) 409s against a newer server version, the
 * generic conflict handling (`query/user-state/useUpdateUserStateItem.ts`'s `onError`) writes the
 * *server's* winning entry into the cache wholesale. For most fields that's correct — but for a
 * field the user just explicitly edited (e.g. they set applicationState to "interviewing" while
 * another tab's write landed first and bumped the version), silently reverting to the server's
 * value would throw away the edit the user is looking at right now.
 *
 * `mergeConflictEntryField` takes the server's winning entry and re-applies the caller's local
 * field(s) on top of it — keeping the server's `version`/`updatedAt` (so the *next* write uses the
 * correct version and doesn't immediately re-conflict) while preserving every other field the
 * server may have that the local write never touched (e.g. tags added from another device).
 *
 * Kept side-effect-free (no queryClient, no Redux) so it can be unit tested directly — this repo
 * has no renderHook harness; see `query/user-state/userStateOptimisticQuery.ts`'s doc comment and
 * `hooks/user-state-conflict-resolvers.ts` for the same precedent applied to other sync hooks.
 *
 * Not every tracked-job conflict uses this: `syncJobIsHiddenToBackend` and the "record viewed /
 * mark applied" writes intentionally accept the server's entry wholesale on conflict (no locally
 * -kept field to protect) — only pass a `patch` for fields where overwriting a fresh local edit
 * would be a real regression, matching the original hook's behavior exactly.
 */
export function mergeConflictEntryField(
  latest: UserStateEntry,
  patch: Record<string, unknown>
): UserStateEntry {
  return {
    ...latest,
    data: {
      ...((latest.data as Record<string, unknown>) ?? {}),
      ...patch,
    },
  };
}
